/**
 * Skincu cart helper.
 *
 * Adds one or more variants to the cart in a single request and then fires the exact
 * event sequence the Stretch theme listens to, so the cart drawer re-renders and opens
 * the same way it does after a native product form submit.
 *
 * Usage: window.skincuAddToCart([{ id: 123, quantity: 1 }], { trigger: buttonElement, discount: 'CODE' })
 */
(function () {
  if (window.skincuAddToCart) return;

  function getRoot() {
    return window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  }

  function isCartPage() {
    const themeSettings = window.themeVariables ? window.themeVariables.settings : {};
    return themeSettings.pageType === 'cart';
  }

  function collectSections() {
    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );
    return sectionsToBundle;
  }

  async function skincuAddToCart(items, options) {
    const settings = options || {};
    const trigger = settings.trigger || null;
    const root = getRoot();

    if (!Array.isArray(items) || items.length === 0) return null;
    if (trigger && trigger.getAttribute('aria-busy') === 'true') return null;
    if (trigger) trigger.setAttribute('aria-busy', 'true');

    // Ask every listening section (cart drawer, cart count, free shipping bar) which
    // section IDs it needs re-rendered with the response.
    const sectionsToBundle = collectSections();

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items, sections: [...sectionsToBundle].join(',') })
      });

      const responseJson = await response.json();

      if (!response.ok) {
        document.dispatchEvent(
          new CustomEvent('toast:show', { detail: { message: responseJson.message, tone: 'error' } })
        );
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return null;
      }

      const themeSettings = window.themeVariables ? window.themeVariables.settings : {};

      if (themeSettings.cartType === 'page' || themeSettings.pageType === 'cart') {
        window.location.href = `${root}cart`;
        return null;
      }

      let renderedSections = responseJson.sections;
      let cartContent = await (await fetch(`${root}cart.js`)).json();

      // Optional bundle discount code: applied once both products are in, next to any code
      // the customer already entered, and the sections are rendered again with it.
      if (settings.discount) {
        const codes = new Set(
          (cartContent.discount_codes || []).filter((entry) => entry.applicable).map((entry) => entry.code)
        );
        codes.add(settings.discount);

        try {
          const updateResponse = await fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: [...codes].join(','), sections: [...sectionsToBundle].join(',') })
          });

          if (updateResponse.ok) {
            const updated = await updateResponse.json();
            if (updated.sections) renderedSections = updated.sections;
            cartContent = await (await fetch(`${root}cart.js`)).json();
          }
        } catch (error) {
          // The products are in the cart either way, the code can still be entered at checkout
        }
      }

      cartContent.sections = renderedSections;

      const addedItems = Object.prototype.hasOwnProperty.call(responseJson, 'items')
        ? responseJson.items
        : [responseJson];

      (trigger || document.documentElement).dispatchEvent(
        new CustomEvent('variant:add', { bubbles: true, detail: { items: addedItems, cart: cartContent } })
      );

      // baseEvent "variant:add" is what makes the cart drawer open itself.
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cartContent } })
      );

      return cartContent;
    } catch (error) {
      console.error(error);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return null;
    } finally {
      if (trigger) trigger.removeAttribute('aria-busy');
    }
  }

  window.skincuAddToCart = skincuAddToCart;

  // Any element carrying data-skincu-add="<json array of line items>" becomes an add button.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-skincu-add]');
    if (!trigger) return;

    let items;

    try {
      items = JSON.parse(trigger.getAttribute('data-skincu-add'));
    } catch (error) {
      return;
    }

    event.preventDefault();
    skincuAddToCart(items, { trigger, discount: (trigger.getAttribute('data-skincu-discount') || '').trim() });
  });

  /**
   * Quantity changes in the cart drawer (plus, minus, typed number and Remove). Handled here
   * so the cart drawer stays open and re-renders, instead of following the links to the
   * cart page. On the cart page itself the links keep working as normal links.
   *
   * Plus and minus read the number currently in the box rather than the link, because the
   * links keep their old target after the drawer updates.
   */
  let lineChangeBusy = false;

  async function skincuChangeLine(key, quantity, lineItem) {
    if (!key || lineChangeBusy) return;
    lineChangeBusy = true;

    const root = getRoot();
    const sectionsToBundle = collectSections();
    const newQuantity = Math.max(0, quantity);

    if (lineItem) {
      lineItem.style.opacity = '0.5';
      lineItem.style.pointerEvents = 'none';
    }

    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: key,
          quantity: newQuantity,
          sections: [...sectionsToBundle].join(',')
        })
      });

      const cartContent = await response.json();

      if (!response.ok) {
        const message = cartContent.description || cartContent.message;

        if (message) {
          document.dispatchEvent(new CustomEvent('toast:show', { detail: { message, tone: 'error' } }));
        }

        throw new Error(message || 'Cart change failed');
      }

      // Keep the number in the box in line with the cart, even if the drawer does not redraw it.
      const updatedLine = (cartContent.items || []).find((item) => item.key === key);

      document.querySelectorAll(`input[data-line-key="${key}"]`).forEach((input) => {
        input.value = updatedLine ? updatedLine.quantity : 0;
      });

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart: cartContent } })
      );

      // Makes sure the drawer redraws with the new quantities and totals.
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (error) {
      console.error(error);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } finally {
      if (lineItem) {
        lineItem.style.opacity = '';
        lineItem.style.pointerEvents = '';
      }

      lineChangeBusy = false;
    }
  }

  window.skincuChangeLine = skincuChangeLine;

  // Remove link
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-skincu-remove]');
    if (!link || isCartPage()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    skincuChangeLine(link.getAttribute('data-skincu-remove'), 0, link.closest('line-item'));
  }, true);

  // Plus and minus links
  document.addEventListener('click', (event) => {
    const button = event.target.closest('.skincu-cartline .quantity-selector__button');
    if (!button || isCartPage()) return;

    const selector = button.closest('quantity-selector');
    const input = selector ? selector.querySelector('input[data-line-key]') : null;
    if (!input) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const buttons = selector.querySelectorAll('.quantity-selector__button');
    const isMinus = buttons.length > 0 && button === buttons[0];
    const current = parseInt(input.value, 10) || 0;
    const step = parseInt(input.getAttribute('step'), 10) || 1;
    const quantity = isMinus ? current - step : current + step;

    skincuChangeLine(input.getAttribute('data-line-key'), quantity, button.closest('line-item'));
  }, true);

  // Typed quantity
  document.addEventListener('change', (event) => {
    const input = event.target.closest('.skincu-cartline input[data-line-key]');
    if (!input || isCartPage()) return;

    event.stopImmediatePropagation();

    const quantity = parseInt(input.value, 10);
    skincuChangeLine(input.getAttribute('data-line-key'), isNaN(quantity) ? 0 : quantity, input.closest('line-item'));
  }, true);

  /**
   * Reserved timer in the cart drawer. The drawer markup is replaced on every cart change, so
   * the end time lives in sessionStorage and each new element simply picks it up again. The
   * countdown starts the first time the element is shown and the banner hides at zero.
   */
  const RESERVE_KEY = 'skincu:reserve-ends-at';

  class SkincuReserveTimer extends HTMLElement {
    connectedCallback() {
      const minutes = Math.max(1, parseInt(this.getAttribute('minutes'), 10) || 10);
      let endsAt = 0;

      try {
        endsAt = parseInt(window.sessionStorage.getItem(RESERVE_KEY), 10) || 0;
      } catch (error) {
        endsAt = 0;
      }

      if (!endsAt) {
        endsAt = Date.now() + minutes * 60 * 1000;

        try {
          window.sessionStorage.setItem(RESERVE_KEY, String(endsAt));
        } catch (error) {
          // Storage can be blocked, the timer then restarts per render.
        }
      }

      this.endsAt = endsAt;
      this.tick();
      this.interval = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.interval);
    }

    tick() {
      const remaining = Math.max(0, Math.round((this.endsAt - Date.now()) / 1000));

      if (remaining === 0) {
        this.hidden = true;
        window.clearInterval(this.interval);
        return;
      }

      // Looked up per tick: the element can be upgraded before its children are parsed.
      const output = this.querySelector('[data-reserve-time]');
      if (!output) return;

      const minutes = String(Math.floor(remaining / 60)).padStart(2, '0');
      const seconds = String(remaining % 60).padStart(2, '0');
      output.textContent = `${minutes}:${seconds}`;
    }
  }

  if (!window.customElements.get('skincu-reserve-timer')) {
    window.customElements.define('skincu-reserve-timer', SkincuReserveTimer);
  }

  /**
   * Stock timer on the product page. midnight counts down to the end of the visitor's day,
   * hours restarts per visit (kept in sessionStorage per product) and date ends at one fixed
   * moment, after which the timer hides itself.
   */
  class SkincuStockTimer extends HTMLElement {
    connectedCallback() {
      const mode = this.getAttribute('mode') || 'midnight';
      this.endsAt = 0;

      if (mode === 'date') {
        this.endsAt = Date.parse(this.getAttribute('end') || '') || 0;
      } else if (mode === 'hours') {
        const hours = Math.max(1, parseInt(this.getAttribute('hours'), 10) || 4);
        const key = `skincu:stock-ends-at:${this.getAttribute('key') || ''}`;

        try {
          this.endsAt = parseInt(window.sessionStorage.getItem(key), 10) || 0;
        } catch (error) {
          this.endsAt = 0;
        }

        if (this.endsAt <= Date.now()) {
          this.endsAt = Date.now() + hours * 3600 * 1000;

          try {
            window.sessionStorage.setItem(key, String(this.endsAt));
          } catch (error) {
            // Storage can be blocked, the timer then restarts per page view.
          }
        }
      }

      this.mode = mode;
      this.tick();
      this.interval = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.interval);
    }

    target() {
      if (this.mode !== 'midnight') return this.endsAt;

      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      return midnight.getTime();
    }

    tick() {
      const remaining = Math.max(0, Math.floor((this.target() - Date.now()) / 1000));

      if (remaining === 0 && this.mode === 'date') {
        this.hidden = true;
        window.clearInterval(this.interval);
        return;
      }

      const output = this.querySelector('[data-stock-time]');
      if (!output) return;

      const pad = (value) => String(value).padStart(2, '0');
      const hours = Math.floor(remaining / 3600);
      const minutes = Math.floor((remaining % 3600) / 60);
      output.textContent = `${pad(hours)}:${pad(minutes)}:${pad(remaining % 60)}`;
      this.setAttribute('data-ready', '');
    }
  }

  if (!window.customElements.get('skincu-stock-timer')) {
    window.customElements.define('skincu-stock-timer', SkincuStockTimer);
  }
})();
/**
 * Bundle gifts (Quantity breaks 2.0).
 *
 * Keeps window.skincuCartState in step with the cart, so the product page does not add a gift
 * twice for the same bundle, and removes gift lines whose bundle is no longer in the cart.
 * A gift line carries the hidden property _skincu_gift with the variant ID of its bundle.
 */
(function () {
  if (window.skincuBundleGifts) return;
  window.skincuBundleGifts = true;

  const root = () => (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');
  let cleaning = false;

  function giftOf(item) {
    return item && item.properties ? item.properties._skincu_gift : null;
  }

  function bundleCounts(items) {
    const counts = {};
    items.forEach((item) => {
      if (giftOf(item)) return;
      const id = String(item.variant_id);
      counts[id] = (counts[id] || 0) + item.quantity;
    });
    return counts;
  }

  /**
   * One gift per bundle: how many of each gift still go along when `adding` more of the bundle
   * are added, so the cart never holds more of a gift than of its bundle (going back and adding
   * again cannot pile up free gifts). Returns [{ id, quantity }] for the gifts still due.
   */
  window.skincuGiftsNeeded = function (cart, bundleId, adding, giftIds) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    const bundle = String(bundleId);
    const bundles = (bundleCounts(items)[bundle] || 0) + (Number(adding) || 1);

    return giftIds
      .filter((giftId, index) => giftIds.indexOf(giftId) === index)
      .map((giftId) => {
        const have = items
          .filter((item) => String(item.variant_id) === String(giftId) && String(giftOf(item)) === bundle)
          .reduce((sum, item) => sum + item.quantity, 0);
        return { id: String(giftId), quantity: Math.max(0, bundles - have) };
      })
      .filter((gift) => gift.quantity > 0);
  };

  // Gifts never outnumber their bundle: extra gift units are taken off (all of them when the
  // bundle has left the cart).
  async function removeOrphans(cart) {
    if (cleaning || !cart || !Array.isArray(cart.items)) return;

    const left = bundleCounts(cart.items);
    const perGift = {};
    const updates = {};

    cart.items.forEach((item) => {
      const bundle = giftOf(item);
      if (!bundle) return;
      const slot = `${item.variant_id}|${bundle}`;
      if (perGift[slot] === undefined) perGift[slot] = left[String(bundle)] || 0;
      const keep = Math.min(item.quantity, perGift[slot]);
      perGift[slot] -= keep;
      if (keep !== item.quantity) updates[item.key] = keep;
    });

    if (!Object.keys(updates).length) return;

    cleaning = true;
    try {
      const response = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (response.ok) {
        window.skincuCartState = await response.json();
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } finally {
      cleaning = false;
    }
  }

  function remember(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    window.skincuCartState = cart;
    removeOrphans(cart);
  }

  function refresh() {
    return fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then(remember)
      .catch(() => {});
  }

  // Re-read the real cart whenever the remembered one may be wrong: after a failed add (gifts
  // that were noted as added are not in the cart), and when the browser shows the page from
  // memory with the back button (the cart may have changed in checkout or another tab).
  window.skincuRefreshCart = refresh;
  document.addEventListener('cart:change', (event) => remember(event.detail && event.detail.cart));
  document.addEventListener('cart:error', refresh);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });

  refresh();
})();
